<template>
  <div class="filter-bar">
    <div class="filter-bar__content">
      <div class="filter-bar__unit-selector">
        <v-select
          :model-value="selectedUnit"
          :items="units"
          item-title="name"
          item-value="id"
          variant="plain"
          density="compact"
          hide-details
          class="filter-bar__unit-select"
          @update:model-value="handleUnitChange"
          @update:menu="isUnitMenuOpen = $event"
        >
          <template #selection="{ item }">
            <span class="filter-bar__unit-text">{{ item.title }}</span>
            <v-icon size="16" color="#000000" class="filter-bar__unit-arrow">
              {{ isUnitMenuOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </template>
        </v-select>
      </div>

      <div class="filter-bar__chips">
        <div class="filter-bar__count">
          <span class="filter-bar__count-text">{{ totalCount }} PATIENTS</span>
        </div>
        <v-chip
          v-for="chip in filterChips"
          :key="chip.id"
          :class="['filter-bar__chip', `filter-bar__chip--${chip.id}`]"
          :color="chip.selected ? '#291758' : chip.color"
          :variant="chip.selected ? 'flat' : 'flat'"
          size="default"
          :rounded="false"
          @click="handleFilterClick(chip.id)"
        >
          <v-icon v-if="chip.selected" start size="18">mdi-check</v-icon>
          {{ chip.label }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import type { PatientCategory, UnitOption } from "@virtual-care/shared";

interface Props {
  totalCount: number;
  filterCounts: {
    overdue: number;
    "discharge-ready": number;
    "condition-discharge": number;
    "new-admit": number;
    other: number;
  };
  selectedFilters: Set<PatientCategory>;
  selectedUnit: string | null;
  units: UnitOption[];
}

interface Emits {
  (e: "toggle-filter", category: PatientCategory): void;
  (e: "unit-change", unitId: string): void;
}

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const isUnitMenuOpen = ref(false);

const filterChips = computed(() => [
  {
    id: "discharge-ready" as PatientCategory,
    label: `${props.filterCounts["discharge-ready"]} Discharge Ready`,
    color: "#EEF5EF",
    selected: props.selectedFilters.has("discharge-ready"),
  },
  {
    id: "condition-discharge" as PatientCategory,
    label: `${props.filterCounts["condition-discharge"]} Conditional Discharge`,
    color: "#FEF3E8",
    selected: props.selectedFilters.has("condition-discharge"),
  },
  {
    id: "new-admit" as PatientCategory,
    label: `${props.filterCounts["new-admit"]} New Admit`,
    color: "#E8EEFB",
    selected: props.selectedFilters.has("new-admit"),
  },
]);

const handleFilterClick = (category: PatientCategory) => {
  emit("toggle-filter", category);
};

const handleUnitChange = (unitId: string) => {
  emit("unit-change", unitId);
};
</script>

<style lang="scss" scoped>
.filter-bar {
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.filter-bar__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 16px 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
  }
}

.filter-bar__unit-selector {
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;
  }
}

.filter-bar__unit-select {
  min-width: 120px;

  :deep(.v-field) {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding: 0;
  }

  :deep(.v-field__input) {
    padding: 0;
    padding-right: 0;
    min-height: auto;
    font-family: "Proxima Nova", sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 1.556em;
    color: #222222;
  }

  :deep(.v-field__outline) {
    display: none !important;
  }

  :deep(.v-field__append-inner) {
    display: none !important;
  }

  :deep(.v-field__append) {
    display: none !important;
  }

  :deep(.v-input__details) {
    display: none !important;
  }

  :deep(.v-select__selection) {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.filter-bar__unit-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.556em;
  color: #222222;
}

.filter-bar__unit-arrow {
  margin-left: 4px;
  transition: transform 0.2s ease;
}

.filter-bar__count {
  margin-right: 12px;
}

.filter-bar__count-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5em;
  color: rgba(0, 0, 0, 0.6);
}

.filter-bar__chips {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    width: 100%;
  }
}

.filter-bar__chip {
  height: 32px;
  border-radius: 8px !important;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 0.9rem !important;
  line-height: 1.5em;

  &.rounded {
    border-radius: 8px !important;
  }

  :deep(.v-chip__content) {
    padding: 6px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem !important;
  }

  &--discharge-ready {
    background-color: #eef5ef !important;
    color: #222222 !important;

    :deep(.v-chip__content) {
      color: #222222 !important;
    }

    &.v-chip--selected {
      background-color: #291758 !important;
      :deep(.v-chip__content) {
        padding: 6px 16px 6px 8px;
        color: #ffffff !important;
      }
    }
  }

  &--condition-discharge {
    background-color: #fef3e8 !important;
    color: #222222 !important;

    :deep(.v-chip__content) {
      color: #222222 !important;
    }

    &.v-chip--selected {
      background-color: #291758 !important;
      :deep(.v-chip__content) {
        padding: 6px 16px 6px 8px;
        color: #ffffff !important;
      }
    }
  }

  &--new-admit {
    background-color: #e8eefb !important;
    color: #222222 !important;

    :deep(.v-chip__content) {
      color: #222222 !important;
    }

    &.v-chip--selected {
      background-color: #291758 !important;
      :deep(.v-chip__content) {
        padding: 6px 16px 6px 8px;
        color: #ffffff !important;
      }
    }
  }

  &--other {
    background-color: #f5f5f5 !important;
    color: #222222 !important;

    :deep(.v-chip__content) {
      color: #222222 !important;
    }

    &.v-chip--selected {
      background-color: #291758 !important;
      :deep(.v-chip__content) {
        padding: 6px 16px 6px 8px;
        color: #ffffff !important;
      }
    }
  }

  &:hover {
    opacity: 0.9;
    transform: none !important;
    width: auto !important;
  }

  :deep(.v-chip__underlay) {
    display: none !important;
  }
}

@media (max-width: 599px) {
  .filter-bar__chip {
    flex: 1 1 calc(50% - 6px);
    min-width: 120px;
  }
}
</style>
